import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Sidebar from "../../Components/Sidebar/Sidebar";
import Header from "../../Components/Header/Header";
import classes from "./Nurses.module.css";
import { 
    FaUsers, 
    FaUserPlus, 
    FaFileImport, 
    FaFileExport, 
    FaSearch, 
    FaRedoAlt,
    FaEllipsisV, 
    FaEye, 
    FaEdit, 
    FaMars, 
    FaVenus,
    FaSpinner,
    FaUserMd,
    FaHeart,
    FaStethoscope
} from "react-icons/fa";
import { BASE_URL } from "../../Components/API/Api";

// --- SKELETON LOADER COMPONENTS ---
const SkeletonStatsCard = () => (
    <div className={classes.skeletonCard}>
        <div className={classes.skeletonIcon}></div>
        <div className={classes.skeletonCardInfo}>
            <div className={classes.skeletonText}></div>
            <div className={classes.skeletonText} style={{ width: '60%' }}></div>
            <div className={classes.skeletonText} style={{ width: '40%' }}></div>
        </div>
    </div>
);

const SkeletonRow = () => (
    <tr className={classes.skeletonRow}>
        <td>
            <div className={classes.skeletonNurseInfo}>
                <div className={classes.skeletonAvatar}></div>
                <div>
                    <div className={classes.skeletonText} style={{ width: '120px' }}></div>
                    <div className={classes.skeletonText} style={{ width: '80px' }}></div>
                </div>
            </div>
        </td>
        <td><div className={classes.skeletonText}></div></td>
        <td><div className={classes.skeletonText}></div></td>
        <td><div className={classes.skeletonText}></div></td>
        <td><div className={classes.skeletonBadge}></div></td>
        <td>
            <div className={classes.skeletonActions}>
                <div className={classes.skeletonActionBtn}></div>
                <div className={classes.skeletonActionBtn}></div>
                <div className={classes.skeletonActionBtn}></div>
            </div>
        </td>
    </tr>
);

function Nurses() {
    const navigate = useNavigate();
    
    const [nurses, setNurses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [department, setDepartment] = useState("");
    const [ward, setWard] = useState("");
    const [status, setStatus] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [stats, setStats] = useState([
        {
            title: "Total Nurses",
            value: "0",
            link: "View all nurses",
            icon: <FaUsers />,
            color: "#2F80ED",
            bg: "#EDF5FF",
        },
        {
            title: "Active Nurses",
            value: "0",
            link: "View active",
            icon: <FaUserPlus />,
            color: "#27AE60",
            bg: "#ECFFF5",
        },
        {
            title: "On Leave",
            value: "0",
            link: "View on leave",
            icon: <FaUserMd />,
            color: "#F2994A",
            bg: "#FFF6EB",
        },
    ]);

    const rowsPerPage = 5;

    // Fetch nurses from API
    const fetchNurses = async () => {
        setLoading(true);
        setError("");
        try {
            const companyId = localStorage.getItem("companyId") || "1";
            
            const response = await axios.get(`${BASE_URL}/api/nurses`, {
                params: {
                    company_id: parseInt(companyId),
                },
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                },
            });

            console.log("Nurses API Response:", response.data);

            let nursesData = [];
            if (response.data && response.data.data) {
                if (Array.isArray(response.data.data)) {
                    nursesData = response.data.data;
                } else if (response.data.data.nurses) {
                    nursesData = response.data.data.nurses;
                } else {
                    nursesData = [response.data.data];
                }
            }

            setNurses(nursesData);
            updateStats(nursesData);

        } catch (error) {
            console.error("Error fetching nurses:", error);
            let errorMsg = "Failed to load nurses. Please try again.";
            if (error.response) {
                if (error.response.status === 401) {
                    errorMsg = "Unauthorized. Please login again.";
                } else if (error.response.data?.message) {
                    const message = error.response.data.message;
                    if (typeof message === 'object') {
                        const firstKey = Object.keys(message)[0];
                        if (firstKey && Array.isArray(message[firstKey])) {
                            errorMsg = message[firstKey][0];
                        } else {
                            errorMsg = JSON.stringify(message);
                        }
                    } else if (typeof message === 'string') {
                        errorMsg = message;
                    }
                }
            } else if (error.request) {
                errorMsg = "Unable to connect to server. Please check your internet connection.";
            }
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    // Update stats
    const updateStats = (nursesData) => {
        const total = nursesData.length;
        const active = nursesData.filter(n => n.status?.toLowerCase() === "active").length;
        const onLeave = nursesData.filter(n => n.status?.toLowerCase() === "on_leave" || n.status?.toLowerCase() === "leave").length;

        setStats([
            {
                title: "Total Nurses",
                value: total.toString(),
                link: "View all nurses",
                icon: <FaUsers />,
                color: "#2F80ED",
                bg: "#EDF5FF",
            },
            {
                title: "Active Nurses",
                value: active.toString(),
                link: "View active",
                icon: <FaUserPlus />,
                color: "#27AE60",
                bg: "#ECFFF5",
            },
            {
                title: "On Leave",
                value: onLeave.toString(),
                link: "View on leave",
                icon: <FaUserMd />,
                color: "#F2994A",
                bg: "#FFF6EB",
            },
        ]);
    };

    useEffect(() => {
        fetchNurses();
    }, []);

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const getAvatar = (nurse) => {
        if (nurse.avatar) return nurse.avatar;
        const gender = nurse.gender?.toLowerCase() === "female" ? "women" : "men";
        const id = nurse.id || Math.floor(Math.random() * 100);
        return `https://randomuser.me/api/portraits/${gender}/${id % 70 + 1}.jpg`;
    };

    const getInitials = (name) => {
        if (!name) return "?";
        const parts = name.split(' ');
        if (parts.length >= 2) {
            return parts[0][0] + parts[1][0];
        }
        return name.substring(0, 2).toUpperCase();
    };

    const getFullName = (nurse) => {
        const firstName = nurse.first_name || '';
        const lastName = nurse.last_name || '';
        const name = nurse.name || '';
        return name || `${firstName} ${lastName}`.trim() || "Unknown";
    };

    // Extract unique departments for filter
    const departments = [...new Set(nurses.map(n => n.department).filter(Boolean))];
    const wards = [...new Set(nurses.map(n => n.ward).filter(Boolean))];
    const statuses = [...new Set(nurses.map(n => n.status).filter(Boolean))];

    const filteredNurses = nurses.filter((nurse) => {
        const keyword = search.toLowerCase();
        const fullName = getFullName(nurse).toLowerCase();

        const searchMatch =
            nurse.first_name?.toLowerCase().includes(keyword) ||
            nurse.last_name?.toLowerCase().includes(keyword) ||
            nurse.email?.toLowerCase().includes(keyword) ||
            nurse.phone?.toLowerCase().includes(keyword) ||
            nurse.nurse_id?.toLowerCase().includes(keyword) ||
            fullName.includes(keyword);

        const departmentMatch =
            department === "" ||
            nurse.department === department;

        const wardMatch =
            ward === "" ||
            nurse.ward === ward;

        const statusMatch =
            status === "" ||
            nurse.status === status;

        return (
            searchMatch &&
            departmentMatch &&
            wardMatch &&
            statusMatch
        );
    });

    const totalPages = Math.ceil(filteredNurses.length / rowsPerPage);
    const indexOfLast = currentPage * rowsPerPage;
    const indexOfFirst = indexOfLast - rowsPerPage;
    const currentNurses = filteredNurses.slice(indexOfFirst, indexOfLast);

    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleView = (nurse) => {
        console.log("Viewing:", nurse);
        navigate(`/nurse/${nurse.id}`);
    };

    const handleEdit = (nurse) => {
        console.log("Editing:", nurse);
        navigate(`/edit-nurse/${nurse.id}`);
    };

    const resetFilters = () => {
        setSearch("");
        setDepartment("");
        setWard("");
        setStatus("");
        setCurrentPage(1);
    };

    const renderSkeletonRows = () => {
        return Array(5).fill(0).map((_, index) => (
            <SkeletonRow key={`skeleton-${index}`} />
        ));
    };

    const renderSkeletonStats = () => {
        return Array(3).fill(0).map((_, index) => (
            <SkeletonStatsCard key={`skeleton-stat-${index}`} />
        ));
    };

    return (
        <div className={classes.page}>
            <Sidebar />
            <div className={classes.main}>
                <Header />
                <div className={classes.content}>
                    {/* TOP SECTION */}
                    <div className={classes.top}>
                        <div>
                            <h2>Nurses</h2>
                            <div className={classes.breadcrumb}>
                                Dashboard
                                <span>&gt;</span>
                                Nurses
                            </div>
                        </div>
                        <div className={classes.actions}>
                            <button className={classes.outline}>
                                <FaFileImport />
                                Import Nurses
                            </button>
                            <button className={classes.outline}>
                                <FaFileExport />
                                Export
                            </button>
                            <button
                                className={classes.primary}
                                onClick={() => navigate("/addNurse")}
                            >
                                + Add New Nurse
                            </button>
                        </div>
                    </div>

                    {/* STATS CARDS */}
                    <div className={classes.cards}>
                        {loading ? (
                            renderSkeletonStats()
                        ) : (
                            stats.map((item, index) => (
                                <div className={classes.card} key={index}>
                                    <div
                                        className={classes.icon}
                                        style={{
                                            background: item.bg,
                                            color: item.color
                                        }}
                                    >
                                        {item.icon}
                                    </div>
                                    <div className={classes.cardInfo}>
                                        <p>{item.title}</p>
                                        <h2>{item.value}</h2>
                                        <button>{item.link}</button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* SEARCH & FILTERS */}
                    <div className={classes.searchContainer}>
                        <div className={classes.searchBox}>
                            <FaSearch className={classes.searchIcon} />
                            <input
                                type="text"
                                placeholder="Search by Name, Email, Phone, ID..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                disabled={loading}
                            />
                        </div>

                        <select
                            value={department}
                            onChange={(e) => setDepartment(e.target.value)}
                            disabled={loading}
                        >
                            <option value="">All Departments</option>
                            {departments.map((dept) => (
                                <option key={dept} value={dept}>{dept}</option>
                            ))}
                        </select>

                        <select
                            value={ward}
                            onChange={(e) => setWard(e.target.value)}
                            disabled={loading}
                        >
                            <option value="">All Wards</option>
                            {wards.map((w) => (
                                <option key={w} value={w}>{w}</option>
                            ))}
                        </select>

                        <select
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            disabled={loading}
                        >
                            <option value="">All Status</option>
                            {statuses.map((stat) => (
                                <option key={stat} value={stat}>{stat}</option>
                            ))}
                        </select>

                        <button
                            className={classes.resetBtn}
                            onClick={resetFilters}
                            disabled={loading}
                        >
                            <FaRedoAlt />
                            Reset
                        </button>
                    </div>

                    {/* ERROR MESSAGE */}
                    {error && (
                        <div className={classes.errorMessage}>
                            <span>{error}</span>
                            <button onClick={fetchNurses} className={classes.retryBtn}>
                                Retry
                            </button>
                        </div>
                    )}

                    {/* TABLE */}
                    <div className={classes.tableContainer}>
                        {loading ? (
                            <table className={classes.table}>
                                <thead>
                                    <tr>
                                        <th>Nurse</th>
                                        <th>Email</th>
                                        <th>Department</th>
                                        <th>Ward</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {renderSkeletonRows()}
                                </tbody>
                            </table>
                        ) : (
                            <table className={classes.table}>
                                <thead>
                                    <tr>
                                        <th>Nurse</th>
                                        <th>Email</th>
                                        <th>Department</th>
                                        <th>Ward</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentNurses.length > 0 ? (
                                        currentNurses.map((nurse) => {
                                            const fullName = getFullName(nurse);
                                            const avatar = getAvatar(nurse);

                                            return (
                                                <tr key={nurse.id}>
                                                    <td>
                                                        <div className={classes.nurseInfo}>
                                                            <img
                                                                src={avatar}
                                                                alt={fullName}
                                                                className={classes.avatar}
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                    e.target.nextSibling.style.display = 'flex';
                                                                }}
                                                            />
                                                            <div className={classes.avatarFallback} style={{ display: 'none' }}>
                                                                {getInitials(fullName)}
                                                            </div>
                                                            <div>
                                                                <h4>{fullName}</h4>
                                                                <span>{nurse.nurse_id || "N/A"}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>{nurse.email || "N/A"}</td>
                                                    <td>{nurse.department || "N/A"}</td>
                                                    <td>{nurse.ward || "N/A"}</td>
                                                    <td>
                                                        <span
                                                            className={`${classes.badge}
                                                            ${nurse.status?.toLowerCase() === "active" 
                                                                ? classes.active 
                                                                : nurse.status?.toLowerCase() === "on_leave" || nurse.status?.toLowerCase() === "leave"
                                                                ? classes.onLeave
                                                                : classes.inactive
                                                            }`}
                                                        >
                                                            {nurse.status || "Pending"}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className={classes.tableActions}>
                                                            <button
                                                                onClick={() => handleView(nurse)}
                                                                className={classes.viewBtn}
                                                                title="View Nurse"
                                                            >
                                                                <FaEye />
                                                            </button>
                                                            <button
                                                                onClick={() => handleEdit(nurse)}
                                                                className={classes.editBtn}
                                                                title="Edit Nurse"
                                                            >
                                                                <FaEdit />
                                                            </button>
                                                            <button
                                                                className={classes.moreBtn}
                                                                title="More Options"
                                                            >
                                                                <FaEllipsisV />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="6" style={{ textAlign: "center", padding: "40px" }}>
                                                No Nurses Found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* PAGINATION */}
                    {!loading && filteredNurses.length > 0 && (
                        <div className={classes.pagination}>
                            <p>
                                Showing
                                <strong>
                                    {" "}
                                    {filteredNurses.length > 0 ? indexOfFirst + 1 : 0}
                                    -
                                    {Math.min(indexOfLast, filteredNurses.length)}
                                </strong>
                                {" "}of{" "}
                                <strong>{filteredNurses.length}</strong>
                                nurses
                            </p>
                            <div className={classes.pages}>
                                <button
                                    onClick={() => goToPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    Previous
                                </button>
                                {Array.from({ length: totalPages }, (_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => goToPage(i + 1)}
                                        className={currentPage === i + 1 ? classes.activePage : ""}
                                    >
                                        {i + 1}
                                    </button>
                                ))}
                                <button
                                    onClick={() => goToPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Nurses;